import { rectFromPoints } from '../core/math';
import type { Point, Rect } from '../core/types';
import { applyCrop, selectionBounds } from '../state/actions';
import type { Tool, ToolContext, ToolPointerEvent } from './types';

const HANDLE_HIT_RADIUS = 9;
const HANDLE_SIZE = 8;

type Edge = 'nw' | 'n' | 'ne' | 'e' | 'se' | 's' | 'sw' | 'w';
type Grab = Edge | 'move' | 'new';

function handlePoints(rect: Rect): Record<Edge, Point> {
  const { x, y, width, height } = rect;
  return {
    nw: { x, y },
    n: { x: x + width / 2, y },
    ne: { x: x + width, y },
    e: { x: x + width, y: y + height / 2 },
    se: { x: x + width, y: y + height },
    s: { x: x + width / 2, y: y + height },
    sw: { x, y: y + height },
    w: { x, y: y + height / 2 },
  };
}

function clampToDocument(rect: Rect, width: number, height: number): Rect {
  const x0 = Math.max(0, Math.min(rect.x, width - 1));
  const y0 = Math.max(0, Math.min(rect.y, height - 1));
  const x1 = Math.min(width, Math.max(x0 + 1, rect.x + rect.width));
  const y1 = Math.min(height, Math.max(y0 + 1, rect.y + rect.height));
  return { x: x0, y: y0, width: x1 - x0, height: y1 - y0 };
}

export const cropTool: Tool = (() => {
  let rect: Rect | null = null;
  let grab: Grab | null = null;
  let startRect: Rect | null = null;
  let startPointer: Point | null = null;

  const reset = (ctx: ToolContext): void => {
    const bounds = selectionBounds();
    rect = bounds ?? { x: 0, y: 0, width: ctx.doc.width, height: ctx.doc.height };
    grab = null;
    ctx.requestRender();
  };

  /**
   * After a crop the document is exactly the old rectangle, so the frame has to be
   * re-based on the new origin. `ctx.doc` still holds the pre-crop size here.
   */
  const commit = (ctx: ToolContext): void => {
    if (!rect) return;
    const applied = rect;
    applyCrop(applied);
    rect = {
      x: 0,
      y: 0,
      width: Math.max(1, Math.round(applied.width)),
      height: Math.max(1, Math.round(applied.height)),
    };
    grab = null;
    startPointer = null;
    startRect = null;
    ctx.requestRender();
  };

  /** Keeps the frame inside the document when it changed size behind our back. */
  const frame = (ctx: ToolContext): Rect | null => {
    if (rect) rect = clampToDocument(rect, ctx.doc.width, ctx.doc.height);
    return rect;
  };

  const hitTest = (event: ToolPointerEvent, ctx: ToolContext): Grab => {
    if (!rect) return 'new';
    const { zoom } = ctx.view;
    for (const [id, point] of Object.entries(handlePoints(rect)) as [Edge, Point][]) {
      const distance = Math.hypot(event.point.x - point.x, event.point.y - point.y) * zoom;
      if (distance <= HANDLE_HIT_RADIUS) return id;
    }
    const inside =
      event.point.x >= rect.x &&
      event.point.y >= rect.y &&
      event.point.x <= rect.x + rect.width &&
      event.point.y <= rect.y + rect.height;
    return inside ? 'move' : 'new';
  };

  return {
    id: 'crop',
    cursor: 'crosshair',
    snaps: true,
    hint: 'Enter to crop, Esc to reset the frame',

    activate(ctx) {
      reset(ctx);
    },

    onPointerDown(event, ctx) {
      grab = hitTest(event, ctx);
      startPointer = event.point;
      startRect = rect ? { ...rect } : null;
      if (grab === 'new') rect = { x: event.point.x, y: event.point.y, width: 0, height: 0 };
      ctx.requestRender();
    },

    onPointerMove(event, ctx) {
      if (!grab || !startPointer) return;

      if (grab === 'new') {
        rect = clampToDocument(
          rectFromPoints(startPointer, event.point),
          ctx.doc.width,
          ctx.doc.height,
        );
        ctx.requestRender();
        return;
      }

      if (!startRect) return;

      if (grab === 'move') {
        rect = clampToDocument(
          {
            ...startRect,
            x: startRect.x + (event.point.x - startPointer.x),
            y: startRect.y + (event.point.y - startPointer.y),
          },
          ctx.doc.width,
          ctx.doc.height,
        );
        ctx.requestRender();
        return;
      }

      let x0 = startRect.x;
      let y0 = startRect.y;
      let x1 = startRect.x + startRect.width;
      let y1 = startRect.y + startRect.height;
      if (grab.includes('w')) x0 = event.point.x;
      if (grab.includes('e')) x1 = event.point.x;
      if (grab.includes('n')) y0 = event.point.y;
      if (grab.includes('s')) y1 = event.point.y;
      rect = clampToDocument(
        rectFromPoints({ x: x0, y: y0 }, { x: x1, y: y1 }),
        ctx.doc.width,
        ctx.doc.height,
      );
      ctx.requestRender();
    },

    onPointerUp(_event, ctx) {
      grab = null;
      startPointer = null;
      startRect = null;
      if (rect && (rect.width < 2 || rect.height < 2)) reset(ctx);
      ctx.requestRender();
    },

    onDoubleClick(_event, ctx) {
      commit(ctx);
    },

    onKeyDown(event, ctx) {
      if (event.key === 'Enter' && rect) {
        commit(ctx);
        return true;
      }
      if (event.key === 'Escape') {
        reset(ctx);
        return true;
      }
      return false;
    },

    cancel(ctx) {
      rect = null;
      grab = null;
      ctx.requestRender();
    },

    drawOverlay(overlay, ctx) {
      const box = frame(ctx);
      if (!box) return;
      const a = overlay.toScreen({ x: box.x, y: box.y });
      const b = overlay.toScreen({ x: box.x + box.width, y: box.y + box.height });
      const docA = overlay.toScreen({ x: 0, y: 0 });
      const docB = overlay.toScreen({ x: ctx.doc.width, y: ctx.doc.height });
      const { ctx: c } = overlay;

      c.save();
      // Dim everything that the crop would discard.
      c.fillStyle = 'rgba(0,0,0,0.5)';
      c.beginPath();
      c.rect(docA.x, docA.y, docB.x - docA.x, docB.y - docA.y);
      c.rect(a.x, a.y, b.x - a.x, b.y - a.y);
      c.fill('evenodd');

      c.strokeStyle = 'rgba(255,255,255,0.95)';
      c.lineWidth = 1;
      c.strokeRect(a.x + 0.5, a.y + 0.5, b.x - a.x, b.y - a.y);

      // Rule-of-thirds guides.
      c.strokeStyle = 'rgba(255,255,255,0.35)';
      for (let i = 1; i < 3; i += 1) {
        const x = a.x + ((b.x - a.x) * i) / 3;
        const y = a.y + ((b.y - a.y) * i) / 3;
        c.beginPath();
        c.moveTo(x, a.y);
        c.lineTo(x, b.y);
        c.moveTo(a.x, y);
        c.lineTo(b.x, y);
        c.stroke();
      }

      c.fillStyle = '#fff';
      c.strokeStyle = 'rgba(0,0,0,0.6)';
      for (const point of Object.values(handlePoints(box))) {
        const p = overlay.toScreen(point);
        c.fillRect(p.x - HANDLE_SIZE / 2, p.y - HANDLE_SIZE / 2, HANDLE_SIZE, HANDLE_SIZE);
        c.strokeRect(p.x - HANDLE_SIZE / 2, p.y - HANDLE_SIZE / 2, HANDLE_SIZE, HANDLE_SIZE);
      }
      c.restore();
    },
  };
})();
